import { useCallback, useEffect } from 'react';
import { useSignalRConnection } from './useSignalRConnection';
import { getDelivererApiUrl } from '../../services/env.service';

interface SalesOrderNotificationMessage {
  status: string;
  message: string;
  timestamp: string;
  metadata: {
    CorrelationId: string;
  };
}

interface useSalesOrderNotificationOptions {
  throwWhenDisconnected?: boolean;
  throwWhenTimeout?: boolean;
  timeout?: number;
}

const hubUrl = getDelivererApiUrl() + '/ordermanagement-notificationHub';
export const useSalesOrderNotification = (
  salesOrderId: string,
  options?: useSalesOrderNotificationOptions
) => {
  const { connection, joinGroup, leaveGroup, isConnected } = useSignalRConnection(hubUrl);

  useEffect(() => {
    if (isConnected) {
      joinGroup(salesOrderId).catch((err) =>
        console.error(`Error joining group ${salesOrderId}:`, err)
      );
    }

    return () => {
      if (isConnected) {
        leaveGroup(salesOrderId).catch((err) =>
          console.error(`Error leaving group ${salesOrderId}:`, err)
        );
      }
    };
  }, [isConnected, salesOrderId, joinGroup, leaveGroup]);

  const receiveNotification = useCallback((): Promise<
    SalesOrderNotificationMessage | undefined
  > => {
    return new Promise((resolve, reject) => {
      if (!isConnected || !connection) {
        if (options?.throwWhenDisconnected) {
          reject(new Error('SignalR connection is not established'));
        } else {
          resolve(undefined);
        }
        return;
      }

      const handler = (message: SalesOrderNotificationMessage) => {
        resolve(message);
        connection.off('ReceiveNotification', handler);
      };

      connection.on('ReceiveNotification', handler);

      // Timeout to prevent waiting indefinitely
      setTimeout(() => {
        connection.off('ReceiveNotification', handler);
        if (options?.throwWhenTimeout) {
          reject(new Error('Timed out waiting for notification'));
        } else {
          resolve(undefined);
        }
      }, options?.timeout || 15000);
    });
  }, [isConnected, connection, options]);

  return {
    receiveNotification
  };
};
